import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Target, Pencil } from "lucide-react";
import { fmtDate, fmtMoney } from "../lib/format";

const EMPTY_FORM = {
  name: "",
  deliverer_id: "",
  target_deliveries: 30,
  available_days: 30,
  bonus_amount: 100,
};

function goalStatus(goal) {
  if (!goal.is_active) return { label: "Inativa", cls: "bg-gray-100 text-gray-700" };
  const starts = goal.starts_at || goal.launched_at || goal.created_at;
  const days = Number(goal.available_days) || 30;
  const endsMs = goal.ends_at
    ? new Date(goal.ends_at).getTime()
    : starts
      ? new Date(starts).getTime() + days * 86400000
      : 0;
  if (endsMs && Date.now() > endsMs) return { label: "Encerrada", cls: "bg-gray-100 text-gray-600" };
  return { label: "Ativa", cls: "bg-green-100 text-green-800" };
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold text-[var(--text-1)]">{label}</Label>
      {hint && <p className="text-[11px] text-muted-foreground leading-snug">{hint}</p>}
      {children}
    </div>
  );
}

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [deliverers, setDeliverers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [evalPeriod, setEvalPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [editGoal, setEditGoal] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const delivererMap = useMemo(
    () => Object.fromEntries(deliverers.map((d) => [d.id, d.name])),
    [deliverers],
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/goals");
      setGoals(Array.isArray(data) ? data : []);
    } catch (err) {
      setGoals([]);
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/users?role=deliverer").then((r) => setDeliverers(r.data || [])).catch(() => {});
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/goals", {
        ...form,
        deliverer_id: form.deliverer_id || null,
      });
      toast.success("Meta lançada com sucesso");
      setForm(EMPTY_FORM);
      if (data?.id) setGoals((prev) => [data, ...prev.filter((g) => g.id !== data.id)]);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const evaluate = async () => {
    try {
      const { data } = await api.post("/goals/evaluate", { period_key: evalPeriod });
      toast.success(`${data.bonuses_created} bônus gerados para ${data.period_key}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const openEdit = (goal) => {
    setEditGoal(goal);
    setEditForm({
      name: goal.name,
      deliverer_id: goal.deliverer_id || "",
      target_deliveries: goal.target_deliveries,
      available_days: goal.available_days ?? 30,
      bonus_amount: goal.bonus_amount,
      is_active: goal.is_active,
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editGoal || !editForm) return;
    setSaving(true);
    try {
      await api.patch(`/goals/${editGoal.id}`, {
        ...editForm,
        deliverer_id: editForm.deliverer_id || null,
      });
      toast.success("Meta atualizada");
      setEditGoal(null);
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl flex items-center gap-2">
            <Target size={24} /> Controle de Metas
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Defina metas para entregadores, acompanhe o progresso e gere bônus em Comissões ao avaliar o período.
          </p>
        </div>
      </div>

      <Card className="p-5 space-y-4">
        <div>
          <h2 className="font-semibold text-sm">Lançar nova meta</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Antes de lançar, defina por quantos dias a meta ficará disponível, quantas entregas são exigidas e o bônus extra de comissão.
          </p>
        </div>
        <form onSubmit={create} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Nome da meta" hint="Identificação interna da campanha (ex.: Meta Julho — Centro).">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="goal-name" />
          </Field>
          <Field label="Entregador" hint="Quem deve atingir a meta. Deixe em branco para aplicar a todos os entregadores.">
            <Select value={form.deliverer_id || "__all__"} onValueChange={(v) => setForm({ ...form, deliverer_id: v === "__all__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Todos os entregadores" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os entregadores</SelectItem>
                {deliverers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Entregas necessárias" hint="Quantidade mínima de entregas concluídas no período que será avaliado.">
            <Input type="number" min={1} value={form.target_deliveries} onChange={(e) => setForm({ ...form, target_deliveries: Number(e.target.value) })} required />
          </Field>
          <Field label="Dias disponíveis" hint="Por quantos dias a meta ficará ativa após o lançamento.">
            <Input type="number" min={1} value={form.available_days} onChange={(e) => setForm({ ...form, available_days: Number(e.target.value) })} required />
          </Field>
          <Field label="Bônus de comissão (R$)" hint="Valor extra creditado automaticamente em Comissões quando a meta for atingida na avaliação.">
            <Input type="number" min={0.01} step={0.01} value={form.bonus_amount} onChange={(e) => setForm({ ...form, bonus_amount: Number(e.target.value) })} required />
          </Field>
          <div className="flex items-end">
            <Button type="submit" className="w-full" data-testid="goal-create-btn">Lançar meta</Button>
          </div>
        </form>
      </Card>

      <Card className="p-5">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Período de avaliação" hint="Mês referência para contar entregas e gerar bônus em Comissões.">
            <Input type="month" value={evalPeriod} onChange={(e) => setEvalPeriod(e.target.value)} className="w-48" data-testid="goal-eval-period" />
          </Field>
          <Button onClick={evaluate} variant="outline" data-testid="goals-evaluate-btn">
            Avaliar metas do período
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Entregadores que atingirem a meta no período selecionado receberão o bônus automaticamente em Comissões, vinculado a esse mês.
        </p>
      </Card>

      <div className="grid gap-3">
        {goals.length === 0 && (
          <Card className="p-8 text-center text-sm text-muted-foreground">Nenhuma meta lançada ainda.</Card>
        )}
        {goals.map((g) => {
          const status = goalStatus(g);
          const progress = g.progress?.[0];
          return (
            <Card key={g.id} className="p-4 flex flex-wrap justify-between items-start gap-3" data-testid={`goal-card-${g.id}`}>
              <div className="space-y-1 min-w-0 flex-1">
                <div className="font-semibold">{g.name}</div>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <div>
                    <span className="font-medium text-[var(--text-2)]">Entregador:</span>{" "}
                    {g.deliverer_id ? delivererMap[g.deliverer_id] || "—" : "Todos"}
                  </div>
                  <div>
                    <span className="font-medium text-[var(--text-2)]">Meta:</span> {g.target_deliveries} entregas
                    {" · "}
                    <span className="font-medium text-[var(--text-2)]">Bônus:</span> {fmtMoney(g.bonus_amount)}
                  </div>
                  <div>
                    <span className="font-medium text-[var(--text-2)]">Disponível:</span>{" "}
                    {g.available_days ?? 30} dias
                    {g.starts_at && g.ends_at && (
                      <> ({fmtDate(g.starts_at)} — {fmtDate(g.ends_at)})</>
                    )}
                  </div>
                  {progress && (
                    <div>
                      <span className="font-medium text-[var(--text-2)]">Progresso ({progress.period_key}):</span>{" "}
                      {progress.delivered_count}/{g.target_deliveries} entregas
                      {progress.bonus_paid && " · bônus pago"}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs uppercase px-2 py-1 rounded font-medium ${status.cls}`}>{status.label}</span>
                <Button size="sm" variant="ghost" onClick={() => openEdit(g)} data-testid={`goal-edit-${g.id}`}>
                  <Pencil size={14} className="mr-1" /> Editar
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!editGoal} onOpenChange={(open) => { if (!open) { setEditGoal(null); setEditForm(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar meta</DialogTitle>
            <DialogDescription>Altere os parâmetros da meta lançada. O prazo de disponibilidade será recalculado se você mudar os dias.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4">
              <Field label="Nome da meta" hint="Identificação interna da campanha.">
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
              </Field>
              <Field label="Entregador" hint="Quem deve atingir a meta.">
                <Select value={editForm.deliverer_id || "__all__"} onValueChange={(v) => setEditForm({ ...editForm, deliverer_id: v === "__all__" ? "" : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os entregadores</SelectItem>
                    {deliverers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Entregas necessárias" hint="Mínimo de entregas no período avaliado.">
                  <Input type="number" min={1} value={editForm.target_deliveries} onChange={(e) => setEditForm({ ...editForm, target_deliveries: Number(e.target.value) })} required />
                </Field>
                <Field label="Dias disponíveis" hint="Duração da meta após o lançamento.">
                  <Input type="number" min={1} value={editForm.available_days} onChange={(e) => setEditForm({ ...editForm, available_days: Number(e.target.value) })} required />
                </Field>
              </div>
              <Field label="Bônus de comissão (R$)" hint="Valor extra em Comissões ao atingir a meta.">
                <Input type="number" min={0.01} step={0.01} value={editForm.bonus_amount} onChange={(e) => setEditForm({ ...editForm, bonus_amount: Number(e.target.value) })} required />
              </Field>
              <Field label="Status" hint="Metas inativas não entram na avaliação.">
                <Select value={editForm.is_active ? "active" : "inactive"} onValueChange={(v) => setEditForm({ ...editForm, is_active: v === "active" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativa</SelectItem>
                    <SelectItem value="inactive">Inativa</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => { setEditGoal(null); setEditForm(null); }}>Cancelar</Button>
                <Button type="submit" disabled={saving}>{saving ? "Salvando…" : "Salvar alterações"}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}